import axios from 'axios';
import { signupAttribution } from '../utils/signupAttribution';
import { waitOutRestarts } from './restartRetry';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    // First, so the handlers below see the answer the server gave once it was back.
    waitOutRestarts(this.client);

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Only force a logout for an expired session on an authenticated
          // request. A 401 from an auth endpoint (wrong password on login,
          // bad code on verify) must reach the page so it can show the inline
          // error instead of reloading it away.
          const url = error.config?.url || '';
          if (!url.includes('/auth/') && localStorage.getItem('token')) {
            localStorage.removeItem('token');
            window.location.href = '/login';
          }
        }
        if (error.response?.status === 403) {
          const errorCode = error.response?.data?.detail?.error_code;
          if (errorCode === 'CHAR_LIMIT_REACHED') {
            window.dispatchEvent(new CustomEvent('char-limit-reached', {
              detail: error.response?.data?.detail?.params
            }));
          }
        }
        return Promise.reject(error);
      }
    );
  }

  setToken(token) {
    this.token = token || null;
    if (token) {
      this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.client.defaults.headers.common['Authorization'];
    }
  }

  async submitQaFeedback({ rating, comment, categories, page, lang }) {
    const response = await this.client.post('/api/qa/feedback', { rating, comment, categories, page, lang });
    return response.data;
  }

  async submitQaVoiceFeedback({ rating, categories, blob, language, page }) {
    const form = new FormData();
    form.append('rating', String(rating));
    form.append('categories', categories.join(','));
    form.append('language', language);
    form.append('page', page);
    form.append('audio', blob, `feedback.${(blob.type || 'audio/webm').split('/')[1].split(';')[0]}`);
    const response = await this.client.post('/api/qa/feedback/voice', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getQaValidatorEmbed(lang) {
    const response = await this.client.get('/api/qa/embed', { params: { lang }, responseType: 'text' });
    return response.data;
  }

  // Auth
  async login(email, password) {
    const response = await this.client.post('/api/auth/login', { email, password });
    return response.data;
  }

  async register(email, password, name, language, acceptTerms) {
    const response = await this.client.post('/api/auth/register', {
      email, password, name, language, accept_terms: acceptTerms, attribution: signupAttribution(),
    });
    return response.data;
  }

  async verify(email, code) {
    const response = await this.client.post('/api/auth/verify', { email, code });
    return response.data;
  }

  async forgotPassword(email) {
    const response = await this.client.post('/api/auth/password/forgot', { email });
    return response.data;
  }

  async resetPassword(email, code, newPassword) {
    const response = await this.client.post('/api/auth/password/reset', { email, code, new_password: newPassword });
    return response.data;
  }

  async changePassword(currentPassword, newPassword) {
    const response = await this.client.post('/api/users/me/password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  }

  // User
  async getCurrentUser() {
    const response = await this.client.get('/api/users/me');
    return response.data;
  }

  async updateUser(userId, data) {
    const response = await this.client.patch(`/api/users/${userId}`, data);
    return response.data;
  }

  async updateCustomPrompt(userId, customPrompt) {
    const response = await this.client.patch(
      `/api/users/${userId}/custom-prompt`,
      null,
      { params: { custom_prompt: customPrompt } }
    );
    return response.data;
  }

  // Workspaces
  async getWorkspace(workspaceId) {
    const response = await this.client.get(`/api/workspaces/${workspaceId}`);
    return response.data;
  }

  // Billing (PAYG character packs)
  async getBillingPacks() {
    const response = await this.client.get('/api/billing/packs');
    return response.data;
  }

  async checkout(payload) {
    // payload: { pack_key } for a preset pack, or { usd_amount } for a custom top-up
    const response = await this.client.post('/api/billing/checkout', payload);
    return response.data;
  }

  async getBillingLedger(skip = 0, limit = 20) {
    const response = await this.client.get('/api/billing/ledger', { params: { skip, limit } });
    return response.data;
  }

  // `month` is "YYYY-MM" (Kyiv time on the server).
  async getBillingMonth(month) {
    const response = await this.client.get('/api/billing/month', { params: { month } });
    return response.data;
  }

  // Chats
  async getChats(skip = 0, limit = 50) {
    const response = await this.client.get('/api/chats', { params: { skip, limit } });
    if (!Array.isArray(response.data)) {
      throw new TypeError(
        `GET /api/chats: expected array, got ${typeof response.data}. ` +
        `Body: ${JSON.stringify(response.data).slice(0, 300)}`
      );
    }
    return response.data;
  }

  async getChat(chatId) {
    const response = await this.client.get(`/api/chats/${chatId}`);
    return response.data;
  }

  async createChat(documentFilename) {
    const response = await this.client.post('/api/chats', {
      document_filename: documentFilename,
    });
    return response.data;
  }

  async deleteChat(chatId) {
    const response = await this.client.delete(`/api/chats/${chatId}`);
    return response.data;
  }

  async renameChat(chatId, name) {
    const response = await this.client.patch(`/api/chats/${chatId}/name`, null, {
      params: { name },
    });
    return response.data;
  }

  // Documents
  async uploadDocument(file, chatId, languageTo, languageVariant = '', context = null, onProgress = null) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('chat_id', chatId);
    formData.append('language_to', languageTo);
    if (languageVariant) {
      formData.append('language_variant', languageVariant);
    }
    if (context) {
      formData.append('translation_context', JSON.stringify(context));
    }

    const response = await this.client.post('/api/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (event) => {
            if (event.total) {
              onProgress(Math.round((event.loaded * 100) / event.total));
            }
          }
        : undefined,
    });
    return response.data;
  }

  async analyzeForCalculator(file, onProgress = null) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post('/api/calculator/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (event) => {
            if (event.total) {
              onProgress(Math.round((event.loaded * 100) / event.total));
            }
          }
        : undefined,
    });
    return response.data;
  }

  // Without `translate` the scan is only recognised into an editable copy: no language, no brief.
  async uploadScan(file, chatId, { translate, languageTo, languageVariant, context }, onProgress = null) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('chat_id', chatId);
    formData.append('translate', translate ? 'true' : 'false');
    if (translate) {
      formData.append('language_to', languageTo);
      if (languageVariant) {
        formData.append('language_variant', languageVariant);
      }
      if (context) {
        formData.append('translation_context', JSON.stringify(context));
      }
    }

    const response = await this.client.post('/api/documents/upload/scan', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (event) => {
            if (event.total) {
              onProgress(Math.round((event.loaded * 100) / event.total));
            }
          }
        : undefined,
    });
    return response.data;
  }

  // A document that failed on our side, tried again in its chat from what it left stored.
  async retryDocument(documentId) {
    const response = await this.client.post(`/api/documents/${documentId}/retry`);
    return response.data;
  }

  async getDocument(documentId) {
    const response = await this.client.get(`/api/documents/${documentId}`);
    return response.data;
  }

  // `uiLanguage` is what the notes a scan's Word file carries on its handwritten values are written in;
  // `fileFormat` sets the copy or translation as Word (docx) or as InDesign (idml).
  async downloadDocument(documentId, fileType, uiLanguage, fileFormat) {
    const response = await this.client.get(`/api/documents/${documentId}/download`, {
      params: { file_type: fileType, ui_language: uiLanguage, file_format: fileFormat },
      responseType: 'blob',
      slowJob: true,
    });
    return response.data;
  }

  // The fonts and notes of the last InDesign download of the copy or translation (`fileType`).
  async getInDesignReport(documentId, fileType) {
    const response = await this.client.get(`/api/documents/${documentId}/indesign-report`, {
      params: { file_type: fileType },
    });
    return response.data;
  }

  async getDocumentDiff(documentId) {
    const response = await this.client.get(`/api/documents/${documentId}/diff`);
    return response.data;
  }

  async getHandwritingPlace(documentId, index) {
    const response = await this.client.get(`/api/documents/${documentId}/handwriting/${index}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async getScanPage(documentId, page = 0) {
    const response = await this.client.get(`/api/documents/${documentId}/scan-page`, {
      params: { page },
      responseType: 'blob',
    });
    return response.data;
  }

  async getOverlayLayout(documentId) {
    const response = await this.client.get(`/api/documents/${documentId}/overlay-layout`);
    return response.data;
  }

  async updateOverlayBoxes(documentId, payload) {
    // payload: { edits, added, deleted, font_family }
    const response = await this.client.patch(
      `/api/documents/${documentId}/overlay-boxes`,
      payload
    );
    return response.data;
  }

  async getComparison(documentId, provider = 'deepl') {
    const response = await this.client.get(`/api/documents/${documentId}/comparison/${provider}`);
    return response.data;
  }

  // Feedbacks
  async submitFeedback(documentId, rating, comment = null, categories = null) {
    const response = await this.client.post('/api/feedbacks', {
      document_id: documentId,
      rating,
      comment,
      categories,
    });
    return response.data;
  }

  async submitVoiceFeedback(documentId, rating, categories = null, audioBlob, language = '') {
    const form = new FormData();
    form.append('document_id', documentId);
    form.append('rating', String(rating));
    form.append('categories', (categories || []).join(','));
    form.append('language', language || '');
    const ext = (audioBlob.type || 'audio/webm').split('/')[1]?.split(';')[0] || 'webm';
    form.append('audio', audioBlob, `feedback.${ext}`);
    const response = await this.client.post('/api/feedbacks/voice', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getDocumentFeedback(documentId) {
    try {
      const response = await this.client.get(`/api/feedbacks/document/${documentId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  // Inline comments anchored to a fragment of the translated text.
  async listTranslationComments(documentId) {
    const response = await this.client.get(`/api/translation-comments/document/${documentId}`);
    return response.data;
  }

  async createTranslationComment(documentId, anchor, comment) {
    const response = await this.client.post('/api/translation-comments', {
      document_id: documentId,
      quote: anchor.quote,
      prefix: anchor.prefix,
      suffix: anchor.suffix,
      start_offset: anchor.start,
      end_offset: anchor.end,
      comment,
    });
    return response.data;
  }

  async deleteTranslationComment(commentId) {
    await this.client.delete(`/api/translation-comments/${commentId}`);
  }

  // Receipt templates (workspace-scoped docx templates with {{ placeholders }})
  async listReceiptTemplates(workspaceId) {
    const response = await this.client.get(`/api/workspaces/${workspaceId}/receipt-templates`);
    return response.data;
  }

  async createReceiptTemplate(workspaceId, file, name) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    const response = await this.client.post(
      `/api/workspaces/${workspaceId}/receipt-templates`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async updateReceiptTemplate(workspaceId, templateId, { name, file } = {}) {
    const formData = new FormData();
    if (name !== undefined && name !== null) formData.append('name', name);
    if (file) formData.append('file', file);
    const response = await this.client.patch(
      `/api/workspaces/${workspaceId}/receipt-templates/${templateId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async deleteReceiptTemplate(workspaceId, templateId) {
    await this.client.delete(`/api/workspaces/${workspaceId}/receipt-templates/${templateId}`);
  }

  async processReceiptBatch(workspaceId, templateId, files, onProgress = null) {
    const formData = new FormData();
    formData.append('template_id', templateId);
    for (const f of files) {
      formData.append('files', f);
    }
    const response = await this.client.post(
      `/api/workspaces/${workspaceId}/receipts/process-batch`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        onUploadProgress: onProgress
          ? (event) => {
              if (event.total) onProgress(Math.round((event.loaded * 100) / event.total));
            }
          : undefined,
      }
    );
    return response.data;
  }

  // QA public share
  async createQaShare(taskId, recipient = null) {
    const response = await this.client.post('/api/qa/share', {
      task_id: taskId,
      recipient: recipient || null,
    });
    return response.data;
  }

  async getSharedQaReport(token) {
    const response = await this.client.get(`/api/qa/shared/${token}`);
    return response.data;
  }

  async listQaShares() {
    const response = await this.client.get('/api/qa/shares');
    return response.data;
  }

  async setQaShareReplied(token, replied) {
    const response = await this.client.patch(`/api/qa/shares/${token}`, { replied });
    return response.data;
  }

  async deleteQaShare(token) {
    await this.client.delete(`/api/qa/shares/${token}`);
  }

  // CRM - the shared inbound-lead queue (sales workspaces only)
  async getCrmMeta() {
    const response = await this.client.get('/api/crm/meta');
    return response.data;
  }

  async listCrmLeads(params) {
    const response = await this.client.get('/api/crm/leads', { params });
    return response.data;
  }

  async getCrmLead(leadId) {
    const response = await this.client.get(`/api/crm/leads/${leadId}`);
    return response.data;
  }

  async fillCrmLeadGreetingNames(leadId) {
    const response = await this.client.post(`/api/crm/leads/${leadId}/greeting-names`);
    return response.data;
  }

  async fillCrmClientGreetingNames(clientId) {
    const response = await this.client.post(`/api/crm/clients/${clientId}/greeting-names`);
    return response.data;
  }

  async createCrmLead(data) {
    const response = await this.client.post('/api/crm/leads', data);
    return response.data;
  }

  async patchCrmLead(leadId, patch) {
    const response = await this.client.patch(`/api/crm/leads/${leadId}`, patch);
    return response.data;
  }

  async addCrmLeadNote(leadId, text) {
    const response = await this.client.post(`/api/crm/leads/${leadId}/notes`, { text });
    return response.data;
  }

  async addCrmLeadPayment(leadId, amount) {
    const response = await this.client.post(`/api/crm/leads/${leadId}/payments`, { amount });
    return response.data;
  }

  async removeCrmLeadPayment(leadId, paymentId) {
    const response = await this.client.delete(`/api/crm/leads/${leadId}/payments/${paymentId}`);
    return response.data;
  }

  async uploadCrmLeadFiles(leadId, kind, files) {
    const form = new FormData();
    form.append('kind', kind);
    files.forEach((file) => form.append('files', file));
    const response = await this.client.post(`/api/crm/leads/${leadId}/files`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async downloadCrmLeadFile(leadId, fileId) {
    const response = await this.client.get(`/api/crm/leads/${leadId}/files/${fileId}`, { responseType: 'blob' });
    return response.data;
  }

  async removeCrmLeadFile(leadId, fileId) {
    const response = await this.client.delete(`/api/crm/leads/${leadId}/files/${fileId}`);
    return response.data;
  }

  async linkCrmLeadToClient(leadId, clientId) {
    const response = await this.client.post(`/api/crm/leads/${leadId}/client`, null, {
      params: clientId ? { client_id: clientId } : undefined,
    });
    return response.data;
  }

  async listCrmClients(params) {
    const response = await this.client.get('/api/crm/clients', { params });
    return response.data;
  }

  async createCrmClient(data) {
    const response = await this.client.post('/api/crm/clients', data);
    return response.data;
  }

  async getCrmClient(clientId) {
    const response = await this.client.get(`/api/crm/clients/${clientId}`);
    return response.data;
  }

  async patchCrmClient(clientId, patch) {
    const response = await this.client.patch(`/api/crm/clients/${clientId}`, patch);
    return response.data;
  }

  async addCrmClientNote(clientId, text) {
    const response = await this.client.post(`/api/crm/clients/${clientId}/notes`, { text });
    return response.data;
  }

  async setCrmClientDocuments(clientId, documentIds) {
    const response = await this.client.put(`/api/crm/clients/${clientId}/documents`, {
      document_ids: documentIds,
    });
    return response.data;
  }

  async searchCrmDocuments(search) {
    const response = await this.client.get('/api/crm/documents', { params: { search } });
    return response.data;
  }

  async getCrmStats(days) {
    const response = await this.client.get('/api/crm/stats', { params: { days } });
    return response.data;
  }
}

export const api = new ApiService();
